import { useMemo, useSyncExternalStore } from "react";
import { contains } from "@/domain";
import { isDeletedRule } from "@/components/rules/rule-presentation";
import type {
  AlertEvent,
  AlertFilters,
  Place,
  PlaceKind,
  Rule,
  RuleType,
  WorkspaceQueries,
} from "@/domain";
// Read the operations team's persisted entities without taking ownership of those stores.
// Storage events cover other tabs; the timer also observes writes in this document.
const keys = [
  "latisent-places-v2",
  "latisent-rules-v2",
  "latisent-alert-events-v2",
];
function snapshot() {
  return JSON.stringify(keys.map((key) => localStorage.getItem(key)));
}
function subscribe(notify: () => void) {
  const timer = window.setInterval(notify, 300);
  window.addEventListener("storage", notify);
  return () => {
    window.clearInterval(timer);
    window.removeEventListener("storage", notify);
  };
}
function records<T>(serialized: string | null): Record<string, T> {
  try {
    return serialized ? (JSON.parse(serialized).state?.records ?? {}) : {};
  } catch {
    return {};
  }
}
/** Overlay only app-owned metadata. Location evidence always comes from the query API. */
export function useOperationOverrides(q: WorkspaceQueries) {
  const value = useSyncExternalStore(subscribe, snapshot);
  return useMemo(() => {
    const raw = JSON.parse(value) as (string | null)[];
    const merge = <T extends { id: string; workspaceId: string }>(
      seed: T[],
      changed: Record<string, T>,
    ) => [
      ...new Map(
        [
          ...seed,
          ...Object.values(changed).filter(
            (v) => v.workspaceId === q.workspace.id,
          ),
        ].map((v) => [v.id, v]),
      ).values(),
    ];
    const places = merge(q.listPlaces(), records<Place>(raw[0]));
    const rules = merge(q.listRules(), records<Rule>(raw[1]));
    const changedAlerts = records<{ event: AlertEvent; read: boolean }>(raw[2]);
    const ranks = new Map<string, number>();
    const alerts = q.listAlerts().map((a) => {
      const day = new Date(a.at).toLocaleDateString("en-CA", {
        timeZone: q.workspace.timeZone,
      });
      const group = `${a.ruleId}|${a.assetId}|${day}`;
      const rank = ranks.get(group) ?? 0;
      ranks.set(group, rank + 1);
      const patch = changedAlerts[`${group}|${rank}`]?.event;
      return patch
        ? {
            ...a,
            status: patch.status,
            acknowledgedAt: patch.acknowledgedAt,
            acknowledgedBy: patch.acknowledgedBy,
            resolvedAt: patch.resolvedAt,
          }
        : a;
    });
    return {
      ...q,
      listPlaces: (kind?: PlaceKind) =>
        places.filter((p) => !kind || p.kind === kind),
      getPlace: (id: string) => places.find((p) => p.id === id),
      getLastSeenPlace: (id: string) => {
        const f = q.getLatestLocation(id);
        return f ? places.find((p) => contains(p, f)) : undefined;
      },
      listRules: (type?: RuleType) =>
        rules.filter((r) => !isDeletedRule(r) && (!type || r.type === type)),
      getRule: (id: string) => rules.find((r) => r.id === id),
      listAlerts: (f: AlertFilters = {}) =>
        alerts
          .filter(
            (a) =>
              (!f.status || a.status === f.status) &&
              (!f.assetId || a.assetId === f.assetId) &&
              (!f.ruleId || a.ruleId === f.ruleId) &&
              (!f.placeId || a.placeId === f.placeId) &&
              (!f.type ||
                rules.find((r) => r.id === a.ruleId)?.type === f.type) &&
              (!f.range || (a.at >= f.range.from && a.at < f.range.to)),
          )
          .slice(0, f.limit ?? Infinity),
      getAlert: (id: string) => alerts.find((a) => a.id === id),
    };
  }, [q, value]);
}
